import React, { useEffect, useRef, useState } from 'react';
import Icon from '~ComponentsImproved/Icon';
import Heading from '~ComponentsImproved/Typography/Heading';
import { ButtonWrapper } from '../ItemButton/Button';
import SubpageIcon from '~Assets/icons/more_horiz.svg';
import OfferIcon from '~Assets/icons/local_offer.svg';
import MessageIcon from '~Assets/icons/send.svg';
import { useLogger } from '~Hooks';
import { useCart } from '~Components/CartProvider';
import { OFFER_TYPE } from '../../../../../../__queries__';
import { getTextColor } from '~Utils/textColor';

interface Props {
  button: any;
  buttonWidth: number;
  setSubPage: (val: any) => void;
  pages: any;
  offers: any;
  isScrolling: boolean;
  addItems: (items: any) => void;
  addMessageOnLastItem: (message: string) => void;
}

const SpecialButton = ({
  button,
  buttonWidth,
  setSubPage,
  pages,
  offers,
  isScrolling,
  addItems,
  addMessageOnLastItem,
}: Props) => {
  const cart = useCart();
  const logger = useLogger('special-button');

  const [touching, setTouching] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleTouchStart = () => {
    setTouching(true);

    timeoutRef.current = setTimeout(() => {
      setTouching(false);
    }, 500);
  };

  const afterClick = () => {
    clearTimeout(timeoutRef.current!);
    setTouching(false);
  };

  useEffect(() => {
    if (isScrolling) {
      setTouching(false);
      clearTimeout(timeoutRef.current!);
    }
  }, [isScrolling]);

  if (!button) {
    logger.warn('Button undefined inside SpecialButton');
  }

  if (button?.pageId) {
    const newPage = pages.find((page: any) => page.id === button.pageId);
    return (
      <ButtonWrapper
        touching={touching}
        onTouchStart={handleTouchStart}
        onTouchEnd={
          touching
            ? () => {
                setSubPage(newPage);
                afterClick();
              }
            : undefined
        }
        color={button.color}
        disabled={false}
        width={`calc(${buttonWidth}% - 8px)`}>
        <Heading level={4} color={getTextColor(button.color)}>
          {button.title}
        </Heading>
        <Icon src={SubpageIcon} color={'rgba(0, 0, 0, 0.2)'} />
      </ButtonWrapper>
    );
  } else if (button?.offerId) {
    const offer = offers.find((offer: any) => offer.id === button.offerId);
    if (!offer) {
      return null;
    }

    const handleOfferClick = () => {
      if (offer.type === OFFER_TYPE.STAGED_BUNDLE || offer.type === OFFER_TYPE.STAGED_INDIVIDUAL) {
        // Validate that the offer has stages
        if (!offer.stages || offer.stages.length === 0) {
          logger.warn('Staged bundle offer has no stages', { offerId: offer.id });
          return;
        }

        logger.info('Opening staged bundle page', {
          offerId: offer.id,
          offerName: offer.name,
          stageCount: offer.stages.length,
        });

        // Create a special subpage object for the staged bundle
        // This will be detected by GridButtons and rendered with StagedBundleGrid
        setSubPage({
          __stagedBundle: true,
          offerName: offer.name || button.title,
          stages: offer.stages,
        });

        afterClick();
      } else {
        addItems(offer.items.map((i: any) => i.id));
        afterClick();
      }
    };

    return (
      <ButtonWrapper
        touching={touching}
        onTouchStart={handleTouchStart}
        onTouchEnd={touching ? handleOfferClick : undefined}
        color={button.color}
        disabled={false}
        width={`calc(${buttonWidth}% - 8px)`}>
        <Heading level={4} color={getTextColor(button.color)}>
          {button.title}
        </Heading>
        <Icon src={OfferIcon} color={'rgba(0, 0, 0, 0.2)'} />
      </ButtonWrapper>
    );
  } else if (button?.messageId) {
    return (
      <ButtonWrapper
        touching={touching}
        onTouchStart={handleTouchStart}
        onTouchEnd={
          touching
            ? () => {
                cart.addMessageToTicket(button.messageId, button.title, button.courseId);
                afterClick();
              }
            : undefined
        }
        color={button.color}
        disabled={false}
        width={`calc(${buttonWidth}% - 8px)`}>
        <Heading level={4} color={getTextColor(button.color)}>
          {button.title}
        </Heading>
        <Icon src={MessageIcon} color={'rgba(0, 0, 0, 0.2)'} />
      </ButtonWrapper>
    );
  } else if (button?.title) {
    return (
      <ButtonWrapper
        touching={touching}
        onTouchStart={handleTouchStart}
        onTouchEnd={
          touching
            ? () => {
                addMessageOnLastItem(button.message);
                afterClick();
              }
            : undefined
        }
        color={button.color}
        disabled={false}
        width={`calc(${buttonWidth}% - 8px)`}>
        <Heading level={4} color={getTextColor(button.color)}>
          {button.title}
        </Heading>
      </ButtonWrapper>
    );
  } else {
    logger.warn('SpecialButton returned null', {
      payload: {
        button,
      },
    });
    return null;
  }
};

export default React.memo(SpecialButton);
